/**
 * Adapters between the form engine's wrapped `FieldValue` and the flat scalars
 * the broker wire carries.
 *
 * The engine never stores a bare value: a text answer is `{ value }`, a
 * multiselect is `{ values: [...], comment }`, a percentile list is
 * `{ props: {...} }`. The broker's `ViewSurfaceField.value` is a scalar. So a
 * multiselect can only cross as its option list, and anything the wire cannot
 * represent must be declared non-proposable rather than flattened into a lie.
 */
import _ from 'lodash';
import { FieldType } from '../../../../form-engine/parsing/parser';
import type {
  BasicValueOption,
  FieldValue,
  FormFieldDef,
} from '../../../../form-engine/types';

/** Field types whose value is a list of chosen options. */
const MULTI_VALUE_TYPES: readonly FieldType[] = [
  FieldType.CheckboxGrouping,
  FieldType.MultiSelect,
];

/**
 * Field types the broker cannot describe as a scalar or an option list, so they
 * are published read-only and can never be a proposal target.
 *
 * Publishing them as editable would let a proposal set a UUID string onto a
 * structure the renderer reads as an object, which fails silently in the
 * control rather than loudly at validation.
 */
const OPAQUE_TYPES: readonly FieldType[] = [
  FieldType.ItemizedPercentileList,
  FieldType.GroupedPercentileList,
  FieldType.JobList,
  FieldType.FieldList,
  FieldType.NaicsClassDescription,
];

export function isMultiValueField(def: FormFieldDef): boolean {
  return MULTI_VALUE_TYPES.includes(def.type);
}

export function isOpaqueField(def: FormFieldDef): boolean {
  return OPAQUE_TYPES.includes(def.type);
}

/** Options a field accepts, or `undefined` for free-text/numeric fields. */
export function optionsOf(def: FormFieldDef): BasicValueOption[] | undefined {
  const options = (def as { valueOptions?: BasicValueOption[] }).valueOptions;
  return Array.isArray(options) && options.length > 0 ? options : undefined;
}

/** The comment a Radio/Checkbox/MultiSelect answer carries alongside its value. */
function commentOf(value: FieldValue | undefined): string | undefined {
  const comment = (value as { comment?: unknown } | undefined)?.comment;
  return typeof comment === 'string' ? comment : undefined;
}

/**
 * The scalar a field currently reads as, for the broker snapshot.
 *
 * A multiselect answers with its options joined, because the wire has one slot
 * and "Fire, Flood" is what the user sees. `display` carries the same string;
 * `value` is not round-trippable for these, which is why they are proposed
 * through their own command rather than by echoing this back.
 */
export function toScalar(
  def: FormFieldDef,
  value: FieldValue | undefined
): string | number | boolean | null {
  if (value == null) {
    return null;
  }
  if (isMultiValueField(def)) {
    return selectedValues(value)
      .map((v) => labelFor(def, v))
      .join(', ');
  }
  const inner = (value as { value?: unknown }).value;
  if (inner == null) {
    return null;
  }
  if (
    typeof inner === 'string' ||
    typeof inner === 'number' ||
    typeof inner === 'boolean'
  ) {
    return inner;
  }
  return null;
}

/** The chosen option values of a multi-value answer, in order. */
export function selectedValues(
  value: FieldValue | undefined
): (string | number)[] {
  const values = (value as { values?: unknown } | undefined)?.values;
  if (!Array.isArray(values)) {
    return [];
  }
  return values
    .map((entry) => (entry as { value?: unknown })?.value ?? entry)
    .filter(
      (v): v is string | number =>
        typeof v === 'string' || typeof v === 'number'
    );
}

/**
 * Loose key for comparing a proposed answer against an option.
 *
 * The extractor answers a question by echoing the option as it appears in the
 * document, which is the option's TEXT and not always character-for-character:
 * a different case, a stray double space, a trailing period. Comparing raw
 * strings rejected answers that were plainly right — `"Virus/malware scanning"`
 * against an option reading `"Virus / malware scanning"` — so matching is done
 * on this key instead of on the literal.
 */
function matchKey(raw: unknown): string {
  return String(raw ?? '')
    .trim()
    .replace(/\s+/g, ' ')
    .toLowerCase();
}

/** The option a proposed answer names, by value or by text. */
function optionFor(
  options: readonly BasicValueOption[],
  proposed: unknown
): BasicValueOption | undefined {
  const exact = options.find(
    (o) => o.value === proposed || o.text === proposed
  );
  if (exact) {
    return exact;
  }
  const key = matchKey(proposed);
  return key
    ? options.find(
        (o) => matchKey(o.value) === key || matchKey(o.text) === key
      )
    : undefined;
}

/** The label the renderer shows for one option value. */
export function labelFor(def: FormFieldDef, raw: unknown): string {
  const options = optionsOf(def);
  const match = options?.find((o) => o.value === raw);
  return String(match?.text ?? raw ?? '');
}

/** What a human reads for a whole answer. Used in every before/after row. */
export function displayOf(
  def: FormFieldDef,
  value: FieldValue | undefined
): string {
  if (value == null) {
    return '—';
  }
  if (isOpaqueField(def)) {
    return isAnswered(value) ? '(structured answer)' : '—';
  }
  if (isMultiValueField(def)) {
    const chosen = selectedValues(value).map((v) => labelFor(def, v));
    return chosen.length > 0 ? chosen.join(', ') : '—';
  }
  const inner = (value as { value?: unknown }).value;
  if (inner == null || inner === '') {
    return '—';
  }
  return labelFor(def, inner);
}

/** True when the answer holds something a user actually chose or typed. */
export function isAnswered(value: FieldValue | undefined): boolean {
  if (value == null) {
    return false;
  }
  const record = value as Record<string, unknown>;
  if (Array.isArray(record.values)) {
    return record.values.length > 0;
  }
  if (record.value !== undefined) {
    return record.value !== '' && record.value !== null;
  }
  return !_.isEmpty(_.omit(record, ['comment']));
}

/**
 * The empty answer for a field, in the shape its control expects.
 *
 * Not one literal for every type. A multi-value control reads `value.values`
 * unguarded — `CheckboxGrouping` calls `.some()` and `.reduce()` on it — so
 * handing it `{ value: '' }` throws in the renderer rather than showing an
 * empty field. Anything that needs a placeholder answer must build it here.
 */
export function emptyAnswer(def: FormFieldDef): FieldValue {
  return (
    isMultiValueField(def) ? { values: [] } : { value: '' }
  ) as unknown as FieldValue;
}

/** An answer the field will take, plus anything that had to be left out. */
export interface CoercedAnswer {
  readonly value: FieldValue;
  /** Options named by the proposal that this field does not offer. */
  readonly dropped: readonly string[];
}

/**
 * Build the engine-shaped answer for a proposed value. A proposed comment (the
 * "Other: ____" text) wins; otherwise the working answer's comment is kept.
 *
 * Returns `null` only when NOTHING usable came out — the caller turns that into
 * an `invalid` proposal the user can see, never a silent drop.
 *
 * A multi-select is per-option, not all-or-nothing. One document answering
 * "MFA, encryption, virus scanning, integrity checks" against a field that
 * offers three of those four is the normal case, and discarding the whole
 * answer over the fourth threw away work the user would have accepted. The
 * three are staged and the fourth is named in `dropped`, which is the same
 * rule the request validator applies to the change-set as a whole.
 */
export function fromProposed(
  def: FormFieldDef,
  proposed: unknown,
  current: FieldValue | undefined,
  proposedComment?: unknown
): CoercedAnswer | null {
  const comment =
    typeof proposedComment === 'string' && proposedComment.trim() !== ''
      ? proposedComment
      : commentOf(current);
  const answer = (body: Record<string, unknown>, dropped: string[] = []) => ({
    value: (comment === undefined
      ? body
      : { ...body, comment }) as unknown as FieldValue,
    dropped,
  });

  if (isOpaqueField(def)) {
    return null;
  }

  if (isMultiValueField(def)) {
    const wanted = Array.isArray(proposed) ? proposed : [proposed];
    const options = optionsOf(def) ?? [];
    const matched: BasicValueOption[] = [];
    const dropped: string[] = [];
    wanted.forEach((w) => {
      // A blank slot is the control's own placeholder for "not ticked", not an
      // answer the field failed to understand.
      if (w === '' || w === null || w === undefined) {
        return;
      }
      const option = optionFor(options, w);
      if (option) {
        // Deduped: two spellings of one option must not tick it twice.
        if (!matched.some((m) => m.value === option.value)) {
          matched.push(option);
        }
        return;
      }
      dropped.push(String(w));
    });
    return matched.length > 0
      ? answer({ values: matched.map((o) => ({ value: o.value })) }, dropped)
      : null;
  }

  const options = optionsOf(def);
  if (options) {
    const match = optionFor(options, proposed);
    return match ? answer({ value: match.value }) : null;
  }

  if (def.type === FieldType.Number || def.type === FieldType.Currency) {
    if (typeof proposed === 'number') {
      return Number.isFinite(proposed) ? answer({ value: proposed }) : null;
    }
    // `Number()` is not a parser. It reads '', '   ', null and [] as 0, so an
    // extraction that found nothing for this question would be staged as a
    // real zero — indistinguishable, on the form, from an answer the document
    // actually gave. Only a string that says something can become a number;
    // everything else is refused and reaches the user as a draft they can see
    // is wrong.
    if (typeof proposed !== 'string' || proposed.trim() === '') {
      return null;
    }
    const numeric = Number(proposed);
    return Number.isFinite(numeric) ? answer({ value: numeric }) : null;
  }

  if (
    typeof proposed === 'string' ||
    typeof proposed === 'number' ||
    typeof proposed === 'boolean'
  ) {
    return answer({ value: proposed });
  }
  return null;
}

/** Answer equality. Deep, because every answer is a wrapper object. */
export function sameAnswer(
  a: FieldValue | undefined,
  b: FieldValue | undefined
): boolean {
  return _.isEqual(a ?? null, b ?? null);
}
